<template>
  <div class="t-card">
    <h5 class="t-card--title">
      {{ $t("question-set.plural") }}
    </h5>
    <b-tabs justified nav-class="nav-tabs-custom" content-class="p-3 text-mutd" v-model="activeTab">
      <b-tab v-if="isBtAdmin">
        <template v-slot:title>
          <span class="d-none d-sm-inline-block">{{ $t("question-set.will-be-reviewed") }}</span>
        </template>
        <div v-if="reviewerPagination">
          <h5 class="t-card--number">
            <div>{{ reviewerPagination.totalDocs }}</div>
          </h5>
          <div class="t-card--small-text" style="margin-bottom: 1rem;">{{ $t("question-set.will-be-reviewed") }}</div>
        </div>
        <div v-if="reviewerPagination">
          <div class="search--input-container mb-3">
            <i class="search--icon iconoir-search"></i>
            <input class="search--input" type="text" v-model="reviewerNameFilter" :placeholder="$t('question-set.search')" />
          </div>
          <div v-if="reviewerPagination">
            <div v-for="questionSet in reviewerPagination.docs" @click="redirectQuestionSet(questionSet)" :key="questionSet._id">
              <question-set-card class="hand" :questionSet="questionSet" />
            </div>
          </div>
          <empty width="100px" v-if="!isReviewerPaginationLoading && (!reviewerPagination || reviewerPagination.docs.length == 0)">
            <h5 class="text-center" id="myText">
              {{ $t("question-set.empty") }}
            </h5>
          </empty>
          <div class="d-flex mt-auto">
            <b-pagination v-model="reviewerRequest.options.page" :total-rows="reviewerPagination.totalDocs" :per-page="reviewerPagination.limit" limit="4" size="sm"></b-pagination>
          </div>
        </div>
        <div v-else>
          <empty width="100px">
            <h5 class="text-center">
              {{ $t("common.empty") }}
            </h5>
          </empty>
        </div>
      </b-tab>
      <b-tab>
        <template v-slot:title>
          <span class="d-none d-sm-inline-block">{{ $t("question-set.will-be-answered") }}</span>
        </template>
        <div v-if="pagination">
          <h5 class="t-card--number">
            <div>{{ pagination.totalDocs }}</div>
            <div class="t-card--small-text" style="margin-bottom: 1rem; ">
              <router-link to="/question-set" tag="span">
                <span class="hand highlight-text">{{ $t("question-set.view-all") }}</span>
              </router-link>
            </div>
          </h5>
          <div class="t-card--small-text" style="margin-bottom: 1rem;">{{ $t("question-set.will-be-answered") }}</div>
          <div class="progress-field">
            <div class="d-flex justify-content-between">
              <div class="progress-field--number">{{ pagination.answeredQuestionSets }} {{ $t("question-set.answered") }}</div>
              <div class="progress-field--percent">{{ completionRate }} %</div>
            </div>
            <div class="progress-field--progress-bar">
              <div class="progress-field--bar" :style="{ width: completionRate + '%' }"></div>
            </div>
          </div>
        </div>
        <div v-if="pagination">
          <div class="search--input-container mb-3">
            <i class="search--icon iconoir-search"></i>
            <input class="search--input" type="text" v-model="nameFilter" :placeholder="$t('question-set.search')" />
          </div>
          <div v-if="pagination">
            <div v-for="questionSet in pagination.docs" @click="redirectQuestionSet(questionSet)" :key="questionSet._id">
              <question-set-card class="hand" :questionSet="questionSet" />
            </div>
          </div>
          <empty width="100px" v-if="!isLoading && (!pagination || pagination.docs.length == 0)">
            <h5 class="text-center" id="myText">
              {{ $t("question-set.empty") }}
            </h5>
          </empty>
          <div class="d-flex mt-auto">
            <b-pagination v-model="request.options.page" :total-rows="pagination.totalDocs" :per-page="pagination.limit" limit="4" size="sm"></b-pagination>
          </div>
        </div>
        <div v-else>
          <empty width="100px">
            <h5 class="text-center">
              {{ $t("announcement.no") }}
            </h5>
          </empty>
        </div>
      </b-tab>
      <b-tab v-if="isFirmAdmin">
        <template v-slot:title>
          <span class="d-none d-sm-inline-block">{{ $t("firm.summary") }}</span>
        </template>
        <firm-admin-column class="mt-3" />
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import FirmAdminColumn from "./firm-admin-column.vue";
import QuestionSetCard from "./question-set-card.vue";

export default {
  components: { QuestionSetCard, FirmAdminColumn },

  data() {
    return {
      nameFilter: undefined,
      reviewerNameFilter: undefined,
      activeTab: 0,
      request: {
        options: { page: 1, limit: 3, sort: { name: "asc" } },
        query: {},
      },
      reviewerRequest: {
        options: { page: 1, limit: 3, sort: { name: "asc" } },
        query: {},
      },
    };
  },

  watch: {
    request: {
      deep: true,
      immediate: true,
      handler() {
        this.debounce(() => this.paginate(), "requested");
      },
    },
    reviewerRequest: {
      deep: true,
      immediate: true,
      handler() {
        this.debounce(() => this.reviewerPaginate(), "reviewer");
      },
    },
    nameFilter(n, o) {
      const localizedField = "name." + this.currentLanguage;
      if (n) {
        this.request.query = { ...this.request.query, [localizedField]: { value: n } };
      } else {
        this.request.query[localizedField] = null;
        delete this.request.query[localizedField];
      }
    },
    reviewerNameFilter(n, o) {
      const localizedField = "name." + this.currentLanguage;
      if (n) {
        this.reviewerRequest.query = { ...this.reviewerRequest.query, [localizedField]: { value: n } };
      } else {
        this.reviewerRequest.query[localizedField] = null;
        delete this.reviewerRequest.query[localizedField];
      }
    },
  },

  computed: {
    isFirmAdmin() {
      return !!this.currentAccount.hasFirmAdminRole;
    },
    isBtAdmin() {
      return !!this.currentAccount.hasBtAdminRole;
    },
    pagination() {
      return this.$store.getters["organizationQuestionSet/myQuestionSetsPagination"];
    },
    isLoading() {
      return this.$store.getters[`organizationQuestionSet/isMyQuestionSetsLoading`];
    },
    reviewerPagination() {
      return this.$store.getters["organizationQuestionSet/reviewerQuestionSetPagination"];
    },
    isReviewerPaginationLoading() {
      return this.$store.getters[`organizationQuestionSet/isReviewerQuestionSetsLoading`];
    },

    completionRate() {
      const answered = Number(this.pagination?.answeredQuestionSets);
      const total = Number(this.pagination?.totalQuestionSets);

      if (!total || isNaN(total) || isNaN(answered)) {
        return 0;
      }

      return Math.floor((answered / total) * 100);
    },
  },

  methods: {
    async paginate() {
      await this.$store.dispatch("organizationQuestionSet/fetchCurrentUserAssignedQuestionSet", {
        ...this.request,
        query: { ...this.request.query, unAnswered: { value: true, isObject: true } },
      });
    },

    async reviewerPaginate() {
      await this.$store.dispatch("organizationQuestionSet/fetchReviewerQuestionSet", this.reviewerRequest);
    },

    async redirectQuestionSet(questionSet) {
      const path = `/question-set/answering/${questionSet._id}`;
      this.selectOrganizationAndGo(questionSet.organization, path, false);
    },
  },
};
</script>

<style lang="scss" scoped>
.highlight-text {
  &:hover {
    color: black;
    font-weight: 500;
  }
}
</style>
